import { useCallback, useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { getCoachScores, getCoachReviews, overrideCoachReview, runCoachReviews, getCoachScoreSettings, saveCoachScoreSettings,
  getQcCalibrationNext, saveQcCalibration, getQcCalibrationReport } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Mic, Settings2, RefreshCw, Star, Flag, Target } from "lucide-react";

const thisMonth = () => new Date().toISOString().slice(0, 7);
const fmtDay = (d) => new Date(d + "T00:00:00").toLocaleDateString(undefined, { weekday: "short", day: "numeric", month: "short" });
const tone = (v) => (v == null ? "text-zinc-600" : v >= 85 ? "text-emerald-300" : v >= 65 ? "text-amber-300" : "text-red-300");
const pct = (v) => (v == null ? "—" : `${Math.round(v)}%`);

/** The old Coach Scores page (now on Accountability: components/AccountabilityCoaches.jsx). Its dialogs are reused there. */
export default function CoachScores() {
  const { activeFacility, user } = useAuth();
  const [month, setMonth] = useState(thisMonth());
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(null);
  const [settings, setSettings] = useState(false);
  const [running, setRunning] = useState(false);
  const [calibrate, setCalibrate] = useState(false);

  const load = useCallback(async () => {
    try {
      setData(await getCoachScores(month));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't load coach scores");
      setData({ coaches: [], weights: {}, parts: {} });
    }
  }, [month]);
  useEffect(() => { load(); }, [load, activeFacility?.id]);

  const runNow = async () => {
    setRunning(true);
    try {
      const r = await runCoachReviews();
      toast.success(`Checked ${r.reviewed} client-day${r.reviewed === 1 ? "" : "s"} for ${fmtDay(r.date)}`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't run the check");
    } finally {
      setRunning(false);
    }
  };

  const w = data?.weights || {};
  return (
    <div className="space-y-6" data-testid="coach-scores">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="eyebrow">Member App · Coaches</div>
          <h2 className="font-display text-3xl font-extrabold mt-1 tracking-tight">Coach Scores</h2>
          <p className="text-sm text-zinc-400 mt-1 max-w-3xl">
            Each accountability coach&apos;s month, from four things: on time to the gym (clock-in, {w.punctuality}%),
            what clients say in the week 2 and week 5 check-ins ({w.feedback}%), replying by 10 am every day ({w.promptness}%),
            and the quality of every reply ({w.quality}%): scored out of 10 on five questions (every meal, used their day, one
            clear ask, written for them, on time), using what the client sent and their meal tracker numbers.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Input type="month" value={month} onChange={(e) => setMonth(e.target.value || thisMonth())} className="w-40" />
          {data?.is_admin && (
            <Button variant="outline" onClick={runNow} disabled={running}><RefreshCw className="mr-1 h-4 w-4" /> Check yesterday now</Button>
          )}
          {data?.is_manager && (
            <Button variant="outline" onClick={() => setCalibrate(true)}><Target className="mr-1 h-4 w-4" /> Calibrate</Button>
          )}
          {user?.role === "master_admin" && (
            <Button variant="outline" onClick={() => setSettings(true)}><Settings2 className="mr-1 h-4 w-4" /> Scoring</Button>
          )}
        </div>
      </div>

      <p className="text-xs text-zinc-500">Every reply is scored automatically at 11:00 (after the catch-up hour), and again after midnight for late replies. Nobody needs to mark anything.</p>

      {data?.judge === "off" && data?.is_admin && (
        <div className="rounded-lg border border-amber-500/40 bg-amber-500/5 px-4 py-3 text-sm text-amber-200">
          The automatic reply-quality check is off (COACH_REVIEW_PROVIDER isn&apos;t set on the server). Replies by 10 am are still
          counted; quality fills in once it&apos;s switched on.
        </div>
      )}

      {data === null ? (
        <div className="card-surface p-8 text-center text-sm text-zinc-500">Loading…</div>
      ) : data.coaches.length === 0 ? (
        <div className="card-surface p-10 text-center text-sm text-zinc-500">
          Nothing yet this month. Scores build up from the day after a 6 Week Challenge starts in the app with a coach.
        </div>
      ) : (
        <div className="card-surface overflow-x-auto">
          <table className="w-full text-sm" data-testid="coach-scores-table">
            <thead className="text-left text-[11px] uppercase tracking-wider text-zinc-500">
              <tr className="border-b border-[#1c1c1c]">
                <th className="px-4 py-3">Coach</th><th className="px-4 py-3">Score</th>
                <th className="px-4 py-3">{data.parts.punctuality}</th><th className="px-4 py-3">{data.parts.feedback}</th>
                <th className="px-4 py-3">{data.parts.promptness}</th><th className="px-4 py-3">{data.parts.quality}</th>
              </tr>
            </thead>
            <tbody>
              {data.coaches.map((c) => (
                <tr key={c.coach_name} onClick={() => setOpen(c.coach_name)} className="cursor-pointer border-b border-[#161616] hover:bg-zinc-900/60">
                  <td className="px-4 py-3 font-semibold text-zinc-100">{c.coach_name || "—"}</td>
                  <td className={`px-4 py-3 font-display text-2xl font-extrabold tabular-nums ${tone(c.score)}`}>{c.score == null ? "—" : Math.round(c.score)}</td>
                  <td className="px-4 py-3">
                    <div className={tone(c.parts.punctuality)}>{pct(c.parts.punctuality)}</div>
                    <div className="text-[11px] text-zinc-500">{c.punctuality.shifts ? `${c.punctuality.late} late · ${c.punctuality.missed} missed of ${c.punctuality.shifts}` : "no roster or clock-ins"}</div>
                  </td>
                  <td className="px-4 py-3">
                    <div className={`flex items-center gap-1 ${tone(c.parts.feedback)}`}>{c.feedback.average ? <><Star className="h-3.5 w-3.5" />{c.feedback.average.toFixed(1)} / 5</> : "—"}</div>
                    <div className="text-[11px] text-zinc-500">{c.feedback.answered} of {c.feedback.sent} check-ins answered</div>
                  </td>
                  <td className="px-4 py-3">
                    <div className={tone(c.parts.promptness)}>{pct(c.parts.promptness)}</div>
                    <div className="text-[11px] text-zinc-500">{c.replies.on_time} of {c.replies.due} days · {c.replies.late} late · {c.replies.missed} missed</div>
                  </td>
                  <td className="px-4 py-3">
                    <div className={tone(c.parts.quality)}>{c.replies.average == null ? "—" : `${c.replies.average} / 10`}</div>
                    <div className="text-[11px] text-zinc-500">
                      <span className="text-emerald-400">{c.replies.green} green</span> · <span className="text-amber-400">{c.replies.amber} amber</span> · <span className="text-red-400">{c.replies.red} red</span>
                      {c.replies.pending ? ` · ${c.replies.pending} not scored` : ""}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {open && <CoachDays coach={open} month={month} canEdit={data?.is_admin} questions={data?.questions || {}} onClose={() => { setOpen(null); load(); }} />}
      {calibrate && <Calibrate onClose={() => setCalibrate(false)} />}
      {settings && <ScoreSettings onClose={() => { setSettings(false); load(); }} />}
    </div>
  );
}

const BAND = { Green: "bg-emerald-500/15 text-emerald-300", Amber: "bg-amber-500/15 text-amber-300", Red: "bg-red-500/15 text-red-300" };
const STATUS = { no_reply: "No reply", not_due: "Not due", unscorable: "Voice note, not transcribed", partial: "Partly scored" };
const FLAG = {
  medical_mention: "Client mentioned something medical", distress: "Client sounded upset", unanswered_question: "Client's question not answered",
  number_given: "Gave a number", exclusion_advised: "Told them to cut a food out", genericness_high: "Reads generic",
  length_anomaly: "Unusual length", unscorable_voice: "Voice note not transcribed",
};
const short = { q1_all_meals: "Meals", q2_used_their_day: "Their day", q3_one_clear_ask: "One ask", q4_written_for_them: "For them", q5_on_time: "On time" };
const val = (v) => (v == null ? "–" : v);
const valTone = (v) => (v === 2 ? "text-emerald-300" : v === 1 ? "text-amber-300" : v === 0 ? "text-red-300" : "text-zinc-600");

/** One coach's month, day by day: every reply's score out of 10, and the evidence behind each question. */
export function CoachDays({ coach, month, canEdit, questions, onClose }) {
  const [data, setData] = useState(null);
  const [tab, setTab] = useState("days");
  const load = useCallback(() => getCoachReviews(coach, month).then(setData).catch(() => toast.error("Couldn't load")), [coach, month]);
  useEffect(() => { load(); }, [load]);

  const correct = async (r, q, value) => {
    const note = window.prompt(`Why is ${short[q]} a ${value}? (shown to the coach)`) || "";
    try {
      await overrideCoachReview(r.id, { scores: { [q]: value }, note });
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't update");
    }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[88vh] max-w-4xl overflow-y-auto bg-[#0f0f0f] border-[#262626] text-zinc-100">
        <DialogHeader>
          <DialogTitle>{coach}</DialogTitle>
          <DialogDescription>Each reply scored 2 / 1 / 0 on five questions, out of 10: 8–10 green, 5–7 amber, 0–4 red. Click a day to see what the client sent and why.</DialogDescription>
        </DialogHeader>
        <div className="flex gap-1">
          {[["days", "Daily replies"], ["feedback", "Client check-ins"]].map(([k, l]) => (
            <button key={k} type="button" onClick={() => setTab(k)}
              className={`rounded px-3 py-1 text-xs ${tab === k ? "bg-zinc-800 text-zinc-100" : "text-zinc-400 hover:text-zinc-200"}`}>{l}</button>
          ))}
        </div>
        {!data ? <div className="py-6 text-sm text-zinc-500">Loading…</div> : tab === "days" ? (
          <table className="w-full text-xs">
            <thead className="text-left text-[10px] uppercase tracking-wider text-zinc-500">
              <tr><th className="py-2 pr-2">Day</th><th className="py-2 pr-2">Client</th>
                {Object.keys(questions).map((q) => <th key={q} className="py-2 pr-1 text-center" title={questions[q]}>{short[q]}</th>)}
                <th className="py-2 pr-2">Total</th><th className="py-2"></th></tr>
            </thead>
            <tbody>
              {data.reviews.map((r) => <DayRow key={r.id} r={r} questions={questions} canEdit={canEdit} onCorrect={(q, v) => correct(r, q, v)} />)}
              {!data.reviews.length && <tr><td colSpan={9} className="py-6 text-center text-zinc-500">No challenge days scored this month.</td></tr>}
            </tbody>
          </table>
        ) : (
          <div className="space-y-2">
            {data.forms.map((f) => (
              <div key={f.id} className="rounded-lg border border-zinc-800 p-3 text-sm">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <span className="font-semibold">{f.client_name} · week {f.week}</span>
                  <span className="text-xs text-zinc-500">{f.answered_at ? `answered ${f.answered_at.slice(0, 10)}` : `sent ${f.date}, not answered`}</span>
                </div>
                {f.answers && (
                  <div className="mt-2 grid gap-1 text-xs text-zinc-300 sm:grid-cols-2">
                    <div>Programme: <span className="text-zinc-100">{f.answers.progress}</span></div>
                    <div>Coaching {f.answers.coaching}/5 · Knowledge {f.answers.knowledge}/5 · Accountability {f.answers.accountability}/5</div>
                    <div>Noticed: <span className="text-zinc-100">{f.answers.changes.join(", ")}</span></div>
                    <div>Learned: <span className="text-zinc-100">{f.answers.learned.join(", ")}</span></div>
                  </div>
                )}
              </div>
            ))}
            {!data.forms.length && <div className="py-6 text-center text-sm text-zinc-500">No check-ins sent this month.</div>}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function DayRow({ r, questions, canEdit, onCorrect }) {
  const [open, setOpen] = useState(false);
  const ci = r.inputs?.check_in;
  const t = r.inputs?.client_profile?.targets || {};
  return (
    <>
      <tr className="cursor-pointer border-b border-[#161616] align-top hover:bg-zinc-900/50" onClick={() => setOpen(!open)}>
        <td className="py-2 pr-2 whitespace-nowrap text-zinc-400">{fmtDay(r.date)}</td>
        <td className="py-2 pr-2">{r.client_name}</td>
        {Object.keys(questions).map((q) => (
          <td key={q} className={`py-2 pr-1 text-center font-bold tabular-nums ${valTone(r.scores?.[q]?.value)}`}>{val(r.scores?.[q]?.value)}</td>
        ))}
        <td className="py-2 pr-2 whitespace-nowrap">
          {r.total != null ? <span className={`rounded px-1.5 py-0.5 font-bold ${BAND[r.band]}`}>{r.total}</span>
            : <span className="text-zinc-500">{STATUS[r.status] || "—"}</span>}
          {r.status === "unscorable" && <Mic className="ml-1 inline h-3 w-3 text-zinc-400" />}
        </td>
        <td className="py-2 text-zinc-500">{r.flags?.length ? <Flag className="h-3.5 w-3.5 text-amber-400" /> : null}</td>
      </tr>
      {open && (
        <tr className="border-b border-[#161616]">
          <td colSpan={9} className="space-y-3 px-2 pb-4 pt-1 text-xs">
            {r.flags?.length > 0 && (
              <div className="flex flex-wrap gap-1">{r.flags.map((f) => <span key={f} className="rounded bg-amber-500/10 px-2 py-0.5 text-amber-300">{FLAG[f] || f}</span>)}
                {r.needs_human_review && <span className="rounded bg-zinc-800 px-2 py-0.5 text-zinc-300">For the Head of Coaching to check</span>}</div>
            )}
            <div className="grid gap-3 md:grid-cols-2">
              <div className="space-y-1">
                <div className="text-[10px] uppercase tracking-wider text-zinc-500">What the client sent</div>
                {ci?.meals?.map((m, i) => (
                  <div key={i} className="text-zinc-300"><span className="capitalize text-zinc-500">{m.slot}{m.time ? ` ${m.time}` : ""}:</span> {m.image_caption || "photo, no estimate"}{m.text ? <span className="italic"> “{m.text}”</span> : null}</div>
                ))}
                {!ci?.meals?.length && <div className="text-zinc-500">No meals</div>}
                {ci?.totals?.kcal != null && (
                  <div className="text-zinc-400">Day: {ci.totals.kcal}{t.kcal ? ` of ${t.kcal}` : ""} kcal · protein {ci.totals.protein_g ?? "?"}{t.protein_g ? ` of ${t.protein_g}` : ""} g</div>
                )}
                <div className="text-zinc-400">Steps {ci?.steps ?? "—"} · sleep {ci?.sleep_hours != null ? `${ci.sleep_hours} h` : "—"}</div>
                {ci?.free_text?.map((x, i) => <div key={i} className="italic text-zinc-300">“{x}”</div>)}
              </div>
              <div className="space-y-1">
                <div className="text-[10px] uppercase tracking-wider text-zinc-500">The reply</div>
                <div className="whitespace-pre-wrap italic text-zinc-200">{r.reply ? `“${r.reply}”` : "No reply"}</div>
              </div>
            </div>
            <div className="space-y-1">
              {Object.keys(questions).map((q) => r.scores?.[q] && (
                <div key={q} className="flex gap-2">
                  <span className={`w-5 shrink-0 text-center font-bold ${valTone(r.scores[q].value)}`}>{val(r.scores[q].value)}</span>
                  <span className="w-44 shrink-0 text-zinc-500">{questions[q]}</span>
                  <span className="text-zinc-200">{r.scores[q].evidence}
                    {q === "q3_one_clear_ask" && r.scores[q].asks?.length > 0 && (
                      <span className="block text-zinc-500">{r.scores[q].asks.map((a) => `“${a.text}” ${a.s ? "S" : "·"}${a.m ? "M" : "·"}${a.t ? "T" : "·"}`).join("  ")}</span>
                    )}
                  </span>
                  {canEdit && (
                    <span className="ml-auto shrink-0 space-x-1 text-zinc-500">
                      {[0, 1, 2].map((v) => <button key={v} type="button" onClick={(e) => { e.stopPropagation(); onCorrect(q, v); }} className="hover:text-zinc-100">{v}</button>)}
                    </span>
                  )}
                </div>
              ))}
            </div>
            <div className="text-[10px] text-zinc-600">{r.judged_by === "manager" ? `Corrected by ${r.judged_by_name}` : r.judged_by === "ai" ? "Scored automatically" : ""}{r.notes?.length ? ` · ${r.notes.join(", ")}` : ""}</div>
          </td>
        </tr>
      )}
    </>
  );
}

/** Gold set (spec §10): the Head of Coaching scores replies blind; the report compares the system to them. */
export function Calibrate({ onClose }) {
  const [item, setItem] = useState(undefined);
  const [report, setReport] = useState(null);
  const [scores, setScores] = useState({});
  const next = useCallback(async () => {
    setScores({});
    try {
      const [n, rep] = await Promise.all([getQcCalibrationNext(), getQcCalibrationReport()]);
      setItem(n);
      setReport(rep);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't load");
    }
  }, []);
  useEffect(() => { next(); }, [next]);
  const r = item?.review;
  const qs = item?.questions || short;
  const noMeals = r && !r.inputs?.check_in?.meals?.length;
  const ready = r && Object.keys(qs).every((q) => scores[q] != null || (q === "q1_all_meals" && noMeals));
  const save = async () => {
    try {
      await saveQcCalibration(r.id, { scores: Object.fromEntries(Object.keys(qs).map((q) => [q, scores[q] ?? null])) });
      next();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't save");
    }
  };
  const ci = r?.inputs?.check_in;
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[88vh] max-w-3xl overflow-y-auto bg-[#0f0f0f] border-[#262626] text-zinc-100">
        <DialogHeader>
          <DialogTitle>Calibrate the scoring</DialogTitle>
          <DialogDescription>Score replies yourself, without seeing the system&apos;s numbers. Aim for 100 across all gyms. The report shows how close the system is.</DialogDescription>
        </DialogHeader>
        {report?.n > 0 && (
          <div className="rounded-lg border border-zinc-800 p-3 text-xs text-zinc-300">
            {report.n} compared · average gap <b>{report.mae_total}</b> points (target under 1.5) · same band <b>{report.band_agreement}%</b> (target 85%+)
            · on time {report.exact_by_question.q5_on_time ?? "—"}% (target 100%) · one clear ask {report.exact_by_question.q3_one_clear_ask ?? "—"}% (target 70%+)
            <span className={`ml-2 font-semibold ${report.passes ? "text-emerald-300" : "text-amber-300"}`}>{report.passes ? "Passing" : "Not there yet"}</span>
          </div>
        )}
        {item === undefined ? <div className="py-6 text-sm text-zinc-500">Loading…</div> : !r ? (
          <div className="py-6 text-center text-sm text-zinc-500">Nothing left to score. {item.scored} done.</div>
        ) : (
          <div className="space-y-3 text-xs">
            <div className="text-zinc-500">{r.coach_name} · {fmtDay(r.date)} · {item.scored} scored so far</div>
            <div className="grid gap-3 md:grid-cols-2">
              <div className="space-y-1">
                <div className="text-[10px] uppercase tracking-wider text-zinc-500">What the client sent</div>
                {ci?.meals?.map((m, i) => <div key={i}><span className="capitalize text-zinc-500">{m.slot}:</span> {m.image_caption || "photo"}{m.text ? <i> “{m.text}”</i> : null}</div>)}
                {!ci?.meals?.length && <div className="text-zinc-500">No meals</div>}
                <div className="text-zinc-400">Steps {ci?.steps ?? "—"} · sleep {ci?.sleep_hours ?? "—"} h</div>
                {ci?.free_text?.map((x, i) => <div key={i} className="italic">“{x}”</div>)}
              </div>
              <div className="space-y-1">
                <div className="text-[10px] uppercase tracking-wider text-zinc-500">The reply · first sent {r.replied_at ? new Date(r.replied_at).toLocaleString() : "—"}</div>
                <div className="whitespace-pre-wrap italic text-zinc-200">“{r.reply}”</div>
              </div>
            </div>
            {Object.entries(qs).map(([q, label]) => (
              <div key={q} className="flex items-center gap-2">
                <span className="w-56 text-zinc-400">{label}</span>
                {[0, 1, 2].map((v) => (
                  <button key={v} type="button" onClick={() => setScores({ ...scores, [q]: v })}
                    className={`h-7 w-9 rounded border ${scores[q] === v ? "border-zinc-200 bg-zinc-200 text-zinc-900" : "border-zinc-700 text-zinc-300"}`}>{v}</button>
                ))}
                {q === "q1_all_meals" && noMeals && <span className="text-zinc-500">no meals: leave blank</span>}
              </div>
            ))}
            <div className="flex justify-end gap-2">
              <Button variant="ghost" onClick={next}>Skip</Button>
              <Button onClick={save} disabled={!ready}>Save and next</Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

export function ScoreSettings({ onClose }) {
  const [s, setS] = useState(null);
  useEffect(() => { getCoachScoreSettings().then(setS).catch(() => toast.error("Couldn't load")); }, []);
  const save = async () => {
    try {
      await saveCoachScoreSettings({ weights: s.weights });
      toast.success("Saved");
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't save");
    }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Scoring</DialogTitle>
          <DialogDescription>How much each part counts (they&apos;re scaled to add up). The reply rubric and its worked examples follow LIFTR&apos;s QC spec v1.0.</DialogDescription>
        </DialogHeader>
        {!s ? <div className="text-sm text-zinc-500">Loading…</div> : (
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              {Object.entries(s.parts).map(([k, label]) => (
                <label key={k} className="space-y-1 text-xs text-zinc-400">{label}
                  <Input type="number" min={0} value={s.weights[k]} onChange={(e) => setS({ ...s, weights: { ...s.weights, [k]: Number(e.target.value) } })} />
                </label>
              ))}
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="ghost" onClick={onClose}>Cancel</Button>
              <Button onClick={save}>Save</Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
